import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { successAlert, errorAlert } from '../../utils/alerts';
import {
  Mail, Server, Send, Save, Eye, EyeOff,
  CheckCircle2, AlertCircle, Info, Code2, Loader2, CheckSquare,
} from 'lucide-react';

const InputField = ({ label, id, required, error, hint, children }) => (
  <div>
    <label htmlFor={id} className="block text-sm font-medium text-gray-700 mb-1.5">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    {children}
    {hint && <p className="mt-1 text-xs text-gray-400">{hint}</p>}
    {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
  </div>
);

const SectionTitle = ({ icon: Icon, label, description }) => (
  <div className="flex items-start gap-3 mb-5 pb-4 border-b border-gray-100">
    <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
      <Icon size={17} className="text-slate-600" strokeWidth={1.75} />
    </div>
    <div>
      <h3 className="font-semibold text-gray-800 text-sm">{label}</h3>
      {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
    </div>
  </div>
);

const VARIABLES = [
  { var: '{{nome}}', desc: 'Nome completo do destinatário' },
  { var: '{{cpf}}', desc: 'CPF/CNPJ do destinatário' },
  { var: '{{codigo}}', desc: 'Código gerado pelo sistema' },
  { var: '{{empresa}}', desc: 'Empresa' },
];

const APPROVAL_VARIABLES = [
  { var: '{{consultor_nome}}', desc: 'Nome do consultor' },
  { var: '{{gestor_nome}}', desc: 'Nome do gestor que aprovou' },
  { var: '{{data}}', desc: 'Data do lançamento' },
  { var: '{{horas}}', desc: 'Quantidade de horas' },
  { var: '{{projeto}}', desc: 'Nome do projeto' },
  { var: '{{cliente}}', desc: 'Nome do cliente' },
];

const APPROVAL_PREVIEW_VALUES = {
  '{{consultor_nome}}': 'João da Silva',
  '{{gestor_nome}}': 'Maria Gestora',
  '{{data}}': '28/07/2026',
  '{{horas}}': '8h00',
  '{{projeto}}': 'Projeto Exemplo',
  '{{cliente}}': 'Cliente Exemplo',
};

const EmailSettings = () => {
  const [form, setForm] = useState({
    smtp_host: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    use_tls: true,
    use_ssl: false,
    from_email: '',
    from_name: '',
    email_subject: 'Confirmação de Cadastro - Kronex',
    email_template: '',
    is_active: true,
    notify_on_approval: false,
    approval_email_subject: 'Horas aprovadas - Kronex',
    approval_email_template: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [errors, setErrors] = useState({});
  const [saveMsg, setSaveMsg] = useState({ type: '', text: '' });
  const [testMsg, setTestMsg] = useState({ type: '', text: '' });
  const [hasConfig, setHasConfig] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewApprovalMode, setPreviewApprovalMode] = useState(false);
  const [testTemplate, setTestTemplate] = useState('default');

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await api.get('/config/email/');
        if (res.status === 204 || !res.data) {
          setHasConfig(false);
        } else {
          const d = res.data;
          setHasConfig(true);
          setForm({
            smtp_host: d.smtp_host || '',
            smtp_port: String(d.smtp_port || '587'),
            smtp_username: d.smtp_username || '',
            smtp_password: d.smtp_password || '',
            use_tls: d.use_tls ?? true,
            use_ssl: d.use_ssl ?? false,
            from_email: d.from_email || '',
            from_name: d.from_name || '',
            email_subject: d.email_subject || 'Confirmação de Cadastro - Kronex',
            email_template: d.email_template || '',
            is_active: d.is_active ?? true,
            notify_on_approval: d.notify_on_approval ?? false,
            approval_email_subject: d.approval_email_subject || 'Horas aprovadas - Kronex',
            approval_email_template: d.approval_email_template || '',
          });
        }
      } catch {
        // config not found
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    setSaveMsg({ type: '', text: '' });
  };

  const handleTlsSsl = (field) => {
    setForm((prev) => ({
      ...prev,
      use_tls: field === 'use_tls' ? !prev.use_tls : false,
      use_ssl: field === 'use_ssl' ? !prev.use_ssl : false,
    }));
  };

  const validate = () => {
    const errs = {};
    if (!form.smtp_host.trim()) errs.smtp_host = 'Servidor SMTP é obrigatório.';
    if (!form.smtp_port) errs.smtp_port = 'Porta é obrigatória.';
    if (!form.smtp_username.trim()) errs.smtp_username = 'Usuário SMTP é obrigatório.';
    if (!hasConfig && !form.smtp_password.trim()) errs.smtp_password = 'Senha é obrigatória no primeiro cadastro.';
    if (!form.from_email.trim()) errs.from_email = 'E-mail do remetente é obrigatório.';
    if (!form.email_template.trim()) errs.email_template = 'Modelo HTML é obrigatório.';
    return errs;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaveMsg({ type: '', text: '' });
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        smtp_port: parseInt(form.smtp_port, 10),
      };
      // If password is masked (unchanged), omit it
      if (form.smtp_password.startsWith('••')) {
        delete payload.smtp_password;
      }
      await api.post('/config/email/', payload);
      setHasConfig(true);
      setSaveMsg({ type: '', text: '' });
      successAlert('Configurações salvas', 'As configurações de e-mail foram atualizadas com sucesso.');
    } catch (err) {
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const fieldErrs = {};
        Object.entries(data).forEach(([k, v]) => {
          fieldErrs[k] = Array.isArray(v) ? v[0] : v;
        });
        setErrors(fieldErrs);
        setSaveMsg({ type: 'error', text: 'Corrija os erros antes de salvar.' });
      } else {
        setSaveMsg({ type: '', text: '' });
        errorAlert('Erro ao salvar', 'Não foi possível salvar as configurações de e-mail.');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async (e) => {
    e.preventDefault();
    if (!testEmail.trim()) {
      setTestMsg({ type: 'error', text: 'Informe um e-mail de destino para o teste.' });
      return;
    }
    setTesting(true);
    setTestMsg({ type: '', text: '' });
    try {
      const res = await api.post('/config/email/test/', {
        recipient_email: testEmail,
        template_type: testTemplate,
      });
      successAlert('E-mail de teste enviado', res.data.detail);
    } catch (err) {
      errorAlert('Erro no envio de teste', err.response?.data?.detail || 'Erro ao enviar e-mail de teste.');
    } finally {
      setTesting(false);
    }
  };

  const insertVariable = (variable, field = 'email_template') => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field] + variable,
    }));
  };

  const approvalPreviewHtml = () => {
    let html = form.approval_email_template || '';
    Object.entries(APPROVAL_PREVIEW_VALUES).forEach(([key, value]) => {
      html = html.split(key).join(value);
    });
    return html;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-[3px] border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      {/* Status banner */}
      {!hasConfig && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <Info size={16} className="text-amber-500 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-700">
            Nenhuma configuração de e-mail encontrada. Preencha o formulário abaixo para habilitar o envio de e-mails do sistema.
          </p>
        </div>
      )}

      <form onSubmit={handleSave} className="space-y-5">
        {/* SMTP Config */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <SectionTitle
            icon={Server}
            label="Servidor SMTP"
            description="Configurações de conexão com o servidor de e-mail"
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="sm:col-span-2">
              <InputField label="Servidor SMTP" id="smtp_host" required error={errors.smtp_host} hint="Ex: smtp.gmail.com, smtp.sendgrid.net">
                <input
                  id="smtp_host"
                  name="smtp_host"
                  type="text"
                  value={form.smtp_host}
                  onChange={handleChange}
                  placeholder="smtp.seuservidor.com"
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.smtp_host ? 'border-red-300' : 'border-gray-300'}`}
                />
              </InputField>
            </div>
            <InputField label="Porta" id="smtp_port" required error={errors.smtp_port} hint="587 (TLS) ou 465 (SSL)">
              <input
                id="smtp_port"
                name="smtp_port"
                type="number"
                value={form.smtp_port}
                onChange={handleChange}
                placeholder="587"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.smtp_port ? 'border-red-300' : 'border-gray-300'}`}
              />
            </InputField>
            <InputField label="Usuário SMTP" id="smtp_username" required error={errors.smtp_username}>
              <input
                id="smtp_username"
                name="smtp_username"
                type="text"
                value={form.smtp_username}
                onChange={handleChange}
                placeholder="seu@email.com"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.smtp_username ? 'border-red-300' : 'border-gray-300'}`}
              />
            </InputField>
            <div className="sm:col-span-2">
              <InputField
                label="Senha SMTP"
                id="smtp_password"
                required={!hasConfig}
                error={errors.smtp_password}
                hint={hasConfig ? 'Deixe em branco para manter a senha atual.' : undefined}
              >
                <div className="relative">
                  <input
                    id="smtp_password"
                    name="smtp_password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.smtp_password}
                    onChange={handleChange}
                    placeholder={hasConfig ? '(mantém a senha atual)' : 'Senha de acesso SMTP'}
                    className={`w-full pl-3 pr-10 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.smtp_password ? 'border-red-300' : 'border-gray-300'}`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </InputField>
            </div>
          </div>

          {/* TLS / SSL */}
          <div className="flex gap-5 mt-5">
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.use_tls}
                onChange={() => handleTlsSsl('use_tls')}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 font-medium">Usar TLS</span>
              <span className="text-xs text-gray-400">(recomendado — porta 587)</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.use_ssl}
                onChange={() => handleTlsSsl('use_ssl')}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 font-medium">Usar SSL</span>
              <span className="text-xs text-gray-400">(porta 465)</span>
            </label>
            <label className="flex items-center gap-2.5 cursor-pointer select-none ml-auto">
              <input
                type="checkbox"
                name="is_active"
                checked={form.is_active}
                onChange={handleChange}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700 font-medium">Configuração ativa</span>
            </label>
          </div>
        </div>

        {/* Remetente */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <SectionTitle
            icon={Mail}
            label="Remetente e Assunto"
            description="Identificação do remetente nos e-mails enviados"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <InputField label="E-mail do Remetente" id="from_email" required error={errors.from_email}>
              <input
                id="from_email"
                name="from_email"
                type="email"
                value={form.from_email}
                onChange={handleChange}
                placeholder="noreply@suaempresa.com"
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.from_email ? 'border-red-300' : 'border-gray-300'}`}
              />
            </InputField>
            <InputField label="Nome do Remetente" id="from_name" error={errors.from_name} hint="Exibido no cliente de e-mail">
              <input
                id="from_name"
                name="from_name"
                type="text"
                value={form.from_name}
                onChange={handleChange}
                placeholder="Kronex"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </InputField>
            <div className="sm:col-span-2">
              <InputField label="Assunto do E-mail" id="email_subject" error={errors.email_subject}>
                <input
                  id="email_subject"
                  name="email_subject"
                  type="text"
                  value={form.email_subject}
                  onChange={handleChange}
                  placeholder="Confirmação de Cadastro - Kronex"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </InputField>
            </div>
          </div>
        </div>

        {/* HTML Template */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <SectionTitle
            icon={Code2}
            label="Modelo HTML do E-mail"
            description="Template do e-mail padrão enviado pelo sistema"
          />

          {/* Variables */}
          <div className="mb-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
              Variáveis disponíveis — clique para inserir no template
            </p>
            <div className="flex flex-wrap gap-2">
              {VARIABLES.map(({ var: v, desc }) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => insertVariable(v)}
                  title={desc}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:border-blue-400 hover:bg-blue-50 rounded-lg text-xs font-mono text-slate-700 hover:text-blue-700 transition-colors"
                >
                  {v}
                  <span className="font-sans text-slate-400 text-xs normal-case">{desc}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Template editor / preview toggle */}
          <div className="flex items-center gap-3 mb-3">
            <button
              type="button"
              onClick={() => setPreviewMode(false)}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${!previewMode ? 'bg-slate-800 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
            >
              Editor
            </button>
            <button
              type="button"
              onClick={() => setPreviewMode(true)}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${previewMode ? 'bg-slate-800 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
            >
              Pré-visualização
            </button>
          </div>

          {previewMode ? (
            <div
              className="border border-gray-200 rounded-lg overflow-hidden"
              style={{ minHeight: '400px' }}
            >
              <iframe
                title="Email preview"
                srcDoc={form.email_template
                  .replace(/\{\{nome\}\}/g, 'João da Silva')
                  .replace(/\{\{cpf\}\}/g, '123.456.789-00')
                  .replace(/\{\{codigo\}\}/g, '847291')
                  .replace(/\{\{empresa\}\}/g, 'Empresa Exemplo S.A.')}
                className="w-full"
                style={{ height: '500px', border: 'none' }}
              />
            </div>
          ) : (
            <textarea
              id="email_template"
              name="email_template"
              rows={20}
              value={form.email_template}
              onChange={handleChange}
              placeholder="<!DOCTYPE html>..."
              className={`w-full px-3 py-2.5 border rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y ${errors.email_template ? 'border-red-300' : 'border-gray-300'}`}
              style={{ minHeight: '280px' }}
            />
          )}
          {errors.email_template && (
            <p className="mt-1 text-xs text-red-600">{errors.email_template}</p>
          )}
        </div>

        {/* E-mail de aprovação de horas */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <SectionTitle
            icon={CheckSquare}
            label="E-mail de Aprovação de Horas"
            description="Notificação enviada ao consultor quando suas horas são aprovadas"
          />

          <label className="flex items-center gap-2.5 cursor-pointer select-none mb-5">
            <input
              type="checkbox"
              name="notify_on_approval"
              checked={form.notify_on_approval}
              onChange={handleChange}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 font-medium">
              Enviar e-mail ao consultor quando as horas forem aprovadas
            </span>
          </label>

          <div className={form.notify_on_approval ? '' : 'opacity-50 pointer-events-none select-none'}>
            <InputField label="Assunto do E-mail de Aprovação" id="approval_email_subject" error={errors.approval_email_subject}>
              <input
                id="approval_email_subject"
                name="approval_email_subject"
                type="text"
                value={form.approval_email_subject}
                onChange={handleChange}
                placeholder="Horas aprovadas - Kronex"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </InputField>

            {/* Variáveis do template de aprovação */}
            <div className="my-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">
                Variáveis disponíveis — clique para inserir no template
              </p>
              <div className="flex flex-wrap gap-2">
                {APPROVAL_VARIABLES.map(({ var: v, desc }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => insertVariable(v, 'approval_email_template')}
                    title={desc}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 hover:border-blue-400 hover:bg-blue-50 rounded-lg text-xs font-mono text-slate-700 hover:text-blue-700 transition-colors"
                  >
                    {v}
                    <span className="font-sans text-slate-400 text-xs normal-case">{desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Editor / preview do template de aprovação */}
            <div className="flex items-center gap-3 mb-3">
              <button
                type="button"
                onClick={() => setPreviewApprovalMode(false)}
                className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${!previewApprovalMode ? 'bg-slate-800 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                Editor
              </button>
              <button
                type="button"
                onClick={() => setPreviewApprovalMode(true)}
                className={`text-xs font-medium px-3 py-1.5 rounded-lg transition-colors ${previewApprovalMode ? 'bg-slate-800 text-white' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                Pré-visualização
              </button>
            </div>

            {previewApprovalMode ? (
              <div
                className="border border-gray-200 rounded-lg overflow-hidden"
                style={{ minHeight: '400px' }}
              >
                <iframe
                  title="Approval email preview"
                  srcDoc={approvalPreviewHtml()}
                  className="w-full"
                  style={{ height: '500px', border: 'none' }}
                />
              </div>
            ) : (
              <textarea
                id="approval_email_template"
                name="approval_email_template"
                rows={16}
                value={form.approval_email_template}
                onChange={handleChange}
                placeholder="<!DOCTYPE html>..."
                className={`w-full px-3 py-2.5 border rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y ${errors.approval_email_template ? 'border-red-300' : 'border-gray-300'}`}
                style={{ minHeight: '240px' }}
              />
            )}
            {errors.approval_email_template && (
              <p className="mt-1 text-xs text-red-600">{errors.approval_email_template}</p>
            )}
          </div>
        </div>

        {/* Save */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            {saveMsg.text && (
              <div className={`flex items-center gap-2 text-sm ${saveMsg.type === 'success' ? 'text-green-700' : 'text-red-600'}`}>
                {saveMsg.type === 'success'
                  ? <CheckCircle2 size={15} />
                  : <AlertCircle size={15} />}
                {saveMsg.text}
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-60 whitespace-nowrap"
            style={{ backgroundColor: '#1e3a5f' }}
          >
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {hasConfig ? 'Salvar Alterações' : 'Salvar Configurações'}
          </button>
        </div>
      </form>

      {/* Test email */}
      {hasConfig && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <SectionTitle
            icon={Send}
            label="Testar Envio de E-mail"
            description="Envie um e-mail de teste com o template atual para verificar a configuração"
          />
          <form onSubmit={handleTest} className="flex flex-col sm:flex-row gap-3">
            <select
              value={testTemplate}
              onChange={(e) => setTestTemplate(e.target.value)}
              className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="default">Template padrão</option>
              <option value="approval">Template de aprovação</option>
            </select>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => { setTestEmail(e.target.value); setTestMsg({ type: '', text: '' }); }}
              placeholder="destinatario@email.com"
              className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="submit"
              disabled={testing}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-60 whitespace-nowrap"
            >
              {testing ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
              Enviar Teste
            </button>
          </form>
          {testMsg.text && (
            <div className={`mt-3 flex items-center gap-2 text-sm ${testMsg.type === 'success' ? 'text-green-700' : 'text-red-600'}`}>
              {testMsg.type === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
              {testMsg.text}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmailSettings;
